import React, { useEffect, useMemo, useState } from "react";
import { Camera, Plus, RotateCw, Save, Video, Users } from "lucide-react";
import "../styles/camera-access-operator.css";
import { fetchLocations } from "../services/locations";
import {
    buildOperatorRecordingUrl,
    createOperatorCamera,
    fetchLocationCameraPermissions,
    fetchOperatorCameraRecordings,
    fetchOperatorCameras,
    saveLocationCameraPermissions,
    updateOperatorCamera,
} from "../services/camera-access";

const EMPTY_FORM = {
    location_id: "",
    title: "",
    descriptions: "",
    rtsp_url: "",
    recording_storage_mode: "cloud",
    local_recordings_dir: "",
    live_hls_url: "",
    snapshot_url: "",
    recording_prefix: "",
    status: true,
};

function formatDateTime(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("uk-UA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatSize(value) {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return "-";
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
}

const CameraAccessOperator = () => {
    const [tab, setTab] = useState("cameras");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [locations, setLocations] = useState([]);
    const [cameras, setCameras] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedCameraId, setSelectedCameraId] = useState(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [cameraForm, setCameraForm] = useState(EMPTY_FORM);
    const [recordings, setRecordings] = useState([]);
    const [selectedRecordingUrl, setSelectedRecordingUrl] = useState("");
    const [permissions, setPermissions] = useState([]);
    const [permissionSearch, setPermissionSearch] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [recordingsLoading, setRecordingsLoading] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const selectedCamera = useMemo(
        () => cameras.find((camera) => camera.id === selectedCameraId) || null,
        [cameras, selectedCameraId]
    );

    const filteredCameras = useMemo(() => {
        const locationId = parseInt(selectedLocation, 10);
        if (!Number.isFinite(locationId)) return cameras;
        return cameras.filter((camera) => Number(camera.location_id) === locationId);
    }, [cameras, selectedLocation]);

    const filteredPermissions = useMemo(() => {
        const q = permissionSearch.trim().toLowerCase();
        if (!q) return permissions;
        return permissions.filter((item) => {
            const haystack = [item.client_name, item.apartment, item.personal_account]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [permissions, permissionSearch]);

    const loadBase = async () => {
        setLoading(true);
        try {
            const [locationItems, cameraItems] = await Promise.all([
                fetchLocations(),
                fetchOperatorCameras(),
            ]);
            const activeLocations = Array.isArray(locationItems) ? locationItems.filter((item) => item.status !== false) : [];
            const allCameras = Array.isArray(cameraItems) ? cameraItems : [];
            setLocations(activeLocations);
            setCameras(allCameras);

            const nextSelectedCameraId = allCameras[0]?.id ?? null;
            setSelectedCameraId((current) => {
                if (isCreatingNew) return current;
                if (current && allCameras.some((camera) => camera.id === current)) return current;
                return nextSelectedCameraId;
            });
        } catch (e) {
            setMessage(`❌ ${e?.message || "Не вдалося завантажити камери"}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBase();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isCreatingNew || !selectedCamera) {
            setCameraForm((prev) => ({
                ...EMPTY_FORM,
                location_id: prev.location_id || selectedLocation || "",
            }));
            return;
        }
        setCameraForm({
            location_id: String(selectedCamera.location_id || ""),
            title: selectedCamera.title || "",
            descriptions: selectedCamera.descriptions || "",
            rtsp_url: selectedCamera.rtsp_url || "",
            recording_storage_mode: selectedCamera.recording_storage_mode || "cloud",
            local_recordings_dir: selectedCamera.local_recordings_dir || "",
            live_hls_url: selectedCamera.live_hls_url || "",
            snapshot_url: selectedCamera.snapshot_url || "",
            recording_prefix: selectedCamera.recording_prefix || "",
            status: selectedCamera.status !== false,
        });
    }, [isCreatingNew, selectedCamera, selectedLocation]);

    useEffect(() => {
        const loadRecordings = async () => {
            if (!selectedCameraId || isCreatingNew) {
                setRecordings([]);
                setSelectedRecordingUrl("");
                return;
            }
            setRecordingsLoading(true);
            try {
                const payload = await fetchOperatorCameraRecordings(selectedCameraId, 120);
                const items = Array.isArray(payload?.items) ? payload.items : [];
                setRecordings(items);
                setSelectedRecordingUrl(items[0]?.download_url ? buildOperatorRecordingUrl(items[0].download_url) : "");
            } catch (e) {
                setRecordings([]);
                setSelectedRecordingUrl("");
                setMessage(`❌ ${e?.message || "Не вдалося завантажити записи"}`);
            } finally {
                setRecordingsLoading(false);
            }
        };
        loadRecordings();
    }, [isCreatingNew, selectedCameraId]);

    useEffect(() => {
        if (isCreatingNew) return;
        if (!filteredCameras.length) return;
        if (filteredCameras.some((camera) => camera.id === selectedCameraId)) return;
        setSelectedCameraId(filteredCameras[0].id);
    }, [filteredCameras, isCreatingNew, selectedCameraId]);

    useEffect(() => {
        const loadPermissions = async () => {
            const locationId = parseInt(selectedLocation, 10);
            if (!Number.isFinite(locationId)) {
                setPermissions([]);
                return;
            }
            setPermissionsLoading(true);
            try {
                const items = await fetchLocationCameraPermissions(locationId);
                setPermissions(Array.isArray(items) ? items : []);
            } catch (e) {
                setPermissions([]);
                setMessage(`❌ ${e?.message || "Не вдалося завантажити доступи"}`);
            } finally {
                setPermissionsLoading(false);
            }
        };
        if (tab === "permissions") loadPermissions();
    }, [selectedLocation, tab]);

    const startNewCamera = () => {
        setIsCreatingNew(true);
        setSelectedCameraId(null);
        setCameraForm({
            ...EMPTY_FORM,
            location_id: String(selectedLocation || locations[0]?.id || ""),
        });
        setRecordings([]);
        setSelectedRecordingUrl("");
    };

    const handleSaveCamera = async () => {
        const payload = {
            location_id: parseInt(cameraForm.location_id, 10),
            title: cameraForm.title.trim(),
            descriptions: cameraForm.descriptions.trim() || null,
            rtsp_url: cameraForm.rtsp_url.trim() || null,
            recording_storage_mode: cameraForm.recording_storage_mode || "cloud",
            local_recordings_dir: cameraForm.recording_storage_mode === "local" ? cameraForm.local_recordings_dir.trim() || null : null,
            live_hls_url: cameraForm.live_hls_url.trim() || null,
            snapshot_url: cameraForm.snapshot_url.trim() || null,
            recording_prefix: cameraForm.recording_prefix.trim() || null,
            status: !!cameraForm.status,
        };
        if (!Number.isFinite(payload.location_id) || !payload.title) {
            setMessage("❌ Вкажіть локацію і назву камери");
            return;
        }

        setSaving(true);
        try {
            const saved = selectedCameraId
                ? await updateOperatorCamera(selectedCameraId, payload)
                : await createOperatorCamera(payload);
            setIsCreatingNew(false);
            await loadBase();
            setSelectedCameraId(saved.id);
            setSelectedLocation(String(saved.location_id));
            setMessage(`✅ Камеру ${selectedCameraId ? "оновлено" : "створено"}`);
        } catch (e) {
            setMessage(`❌ ${e?.message || "Не вдалося зберегти камеру"}`);
        } finally {
            setSaving(false);
        }
    };

    const handlePermissionToggle = (clientId, apartment, enabled) => {
        setPermissions((prev) => prev.map((item) => (
            item.client_id === clientId && (item.apartment || "") === (apartment || "")
                ? { ...item, enabled }
                : item
        )));
    };

    const handleSavePermissions = async () => {
        const locationId = parseInt(selectedLocation, 10);
        if (!Number.isFinite(locationId)) {
            setMessage("❌ Виберіть локацію для налаштування доступів");
            return;
        }
        setSaving(true);
        try {
            await saveLocationCameraPermissions(
                permissions.map((item) => ({
                    client_id: item.client_id,
                    location_id: locationId,
                    apartment: item.apartment || null,
                    enabled: !!item.enabled,
                }))
            );
            setMessage("✅ Доступи до історії камер збережено");
        } catch (e) {
            setMessage(`❌ ${e?.message || "Не вдалося зберегти доступи"}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="camera-access-operator-container">
            <div className="config-header caop-page-header">
                <div>
                    <h2>Камери та відеоархів</h2>
                    <p className="caop-subtitle">Оператор бачить live та історію за 5 днів по всіх локаціях мережі. Клієнтам доступна тільки історія за 3 дні і лише за дозволеними квартирами.</p>
                </div>
                <div className="header-buttons page-header-actions">
                    {isMobile && tab === "cameras" ? (
                        <button className="add-btn toolbar-icon-btn" type="button" onClick={startNewCamera} title="Додати" aria-label="Додати">
                            <Plus size={16} />
                        </button>
                    ) : null}
                    <button className="add-btn refresh toolbar-icon-btn" type="button" onClick={loadBase} disabled={loading || saving} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                </div>
            </div>

            {message && <div className="message-box">{message}</div>}

            <div className="caop-toolbar">
                <div className="caop-tabs">
                    <button type="button" className={`caop-tab ${tab === "cameras" ? "active" : ""}`} onClick={() => setTab("cameras")}>
                        <Camera size={16} /> Камери
                    </button>
                    <button type="button" className={`caop-tab ${tab === "permissions" ? "active" : ""}`} onClick={() => setTab("permissions")}>
                        <Users size={16} /> Доступ квартир
                    </button>
                </div>

                <label className="caop-select-label">
                    Локація
                    <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                        <option value="">Всі локації</option>
                        {locations.map((loc) => (
                            <option key={loc.id} value={loc.id}>
                                {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ") || `Локація ${loc.id}`}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {tab === "cameras" ? (
                <div className="caop-layout">
                    <aside className={`caop-sidebar ${isMobile ? "caop-sidebar--mobile" : ""}`}>
                        {!isMobile ? <button type="button" className="caop-new-btn" onClick={startNewCamera}>+ Нова камера</button> : null}
                        {filteredCameras.map((camera) => (
                            <button
                                key={camera.id}
                                type="button"
                                className={`caop-camera-item ${camera.id === selectedCameraId ? "active" : ""}`}
                                onClick={() => {
                                    setIsCreatingNew(false);
                                    setSelectedCameraId(camera.id);
                                }}
                            >
                                <span className="caop-camera-title">{camera.title}</span>
                                {isMobile ? (
                                    <span className={`caop-camera-mobile-meta ${camera.status ? "on" : "off"}`}>{camera.status ? "Активна" : "Вимкнена"}</span>
                                ) : (
                                    <>
                                        <span className="caop-camera-meta">{[camera.street, camera.house_number, camera.location_name].filter(Boolean).join(" ")}</span>
                                        <span className={`caop-storage-status ${camera.storage_accessible ? "ok" : "bad"}`}>
                                            {camera.recording_storage_mode === "local" ? "Local" : "Cloud"}: {camera.storage_accessible ? "доступно" : "недоступно"}
                                        </span>
                                        <span className={`caop-camera-status ${camera.status ? "on" : "off"}`}>{camera.status ? "Активна" : "Вимкнена"}</span>
                                    </>
                                )}
                            </button>
                        ))}
                    </aside>

                    <section className="caop-main">
                        <div className="caop-card caop-form-card">
                            <div className="caop-card-head">
                                <h3>{selectedCameraId ? "Редагування камери" : "Створення камери"}</h3>
                                <button type="button" className="add-btn refresh" onClick={handleSaveCamera} disabled={saving}>
                                    <Save size={16} /> Зберегти
                                </button>
                            </div>
                            {selectedCamera ? (
                                <div className={`caop-storage-banner ${selectedCamera.storage_accessible ? "ok" : "bad"}`}>
                                    <strong>{selectedCamera.recording_storage_mode === "local" ? "Local storage" : "Cloud storage"}</strong>
                                    <span>{selectedCamera.storage_status_text || (selectedCamera.storage_accessible ? "Storage is available" : "Storage is unavailable")}</span>
                                    {selectedCamera.recordings_root ? <span>{selectedCamera.recordings_root}</span> : null}
                                </div>
                            ) : null}
                            <div className="caop-form-grid">
                                <label>
                                    Локація
                                    <select value={cameraForm.location_id} onChange={(e) => setCameraForm((prev) => ({ ...prev, location_id: e.target.value }))}>
                                        <option value="">Виберіть локацію</option>
                                        {locations.map((loc) => (
                                            <option key={loc.id} value={loc.id}>
                                                {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ") || `Локація ${loc.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Назва камери
                                    <input value={cameraForm.title} onChange={(e) => setCameraForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Вхід 1" />
                                </label>
                                <label className="caop-form-span-2">
                                    Опис
                                    <input value={cameraForm.descriptions} onChange={(e) => setCameraForm((prev) => ({ ...prev, descriptions: e.target.value }))} placeholder="Під'їзд, основний вхід" />
                                </label>
                                <label className="caop-form-span-2">
                                    RTSP URL
                                    <input value={cameraForm.rtsp_url} onChange={(e) => setCameraForm((prev) => ({ ...prev, rtsp_url: e.target.value }))} placeholder="rtsp://user:pass@192.168.11.10:554/stream1" />
                                </label>
                                <label>
                                    Режим запису
                                    <select value={cameraForm.recording_storage_mode} onChange={(e) => setCameraForm((prev) => ({ ...prev, recording_storage_mode: e.target.value, local_recordings_dir: e.target.value === "local" ? prev.local_recordings_dir : "" }))}>
                                        <option value="cloud">Централізовано (cloud/server)</option>
                                        <option value="local">Локально в локації</option>
                                    </select>
                                </label>
                                <label className="caop-form-span-2">
                                    Локальний каталог записів
                                    <input
                                        value={cameraForm.local_recordings_dir}
                                        onChange={(e) => setCameraForm((prev) => ({ ...prev, local_recordings_dir: e.target.value }))}
                                        placeholder="/mnt/location-15/camera-recordings/enter-150-1"
                                        disabled={cameraForm.recording_storage_mode !== "local"}
                                    />
                                </label>
                                <label className="caop-form-span-2">
                                    Live HLS URL
                                    <input value={cameraForm.live_hls_url} onChange={(e) => setCameraForm((prev) => ({ ...prev, live_hls_url: e.target.value }))} placeholder="https://video.example/live/entrance.m3u8" />
                                </label>
                                <label className="caop-form-span-2">
                                    Snapshot URL
                                    <input value={cameraForm.snapshot_url} onChange={(e) => setCameraForm((prev) => ({ ...prev, snapshot_url: e.target.value }))} placeholder="https://video.example/snapshot.jpg" />
                                </label>
                                <label>
                                    Префікс записів
                                    <input value={cameraForm.recording_prefix} onChange={(e) => setCameraForm((prev) => ({ ...prev, recording_prefix: e.target.value }))} placeholder="entrance-1" />
                                </label>
                                <label className="caop-checkbox-label">
                                    <input type="checkbox" checked={!!cameraForm.status} onChange={(e) => setCameraForm((prev) => ({ ...prev, status: e.target.checked }))} />
                                    Камера активна
                                </label>
                            </div>
                        </div>

                        <div className="caop-card">
                            <div className="caop-card-head">
                                <h3><Video size={18} /> Live та записи</h3>
                                {selectedCamera?.live_hls_url ? (
                                    <a className="caop-live-link" href={selectedCamera.live_hls_url} target="_blank" rel="noreferrer">Відкрити live</a>
                                ) : null}
                            </div>

                            {selectedRecordingUrl ? (
                                <video className="caop-video" controls preload="metadata" src={selectedRecordingUrl} />
                            ) : (
                                <div className="caop-empty">{recordingsLoading ? "Завантаження записів..." : "Для цієї камери поки немає записів за останні 5 днів."}</div>
                            )}

                            <div className="caop-recordings-list">
                                {recordings.map((item) => {
                                    const url = buildOperatorRecordingUrl(item.download_url);
                                    return (
                                        <div key={item.id} className={`caop-recording-item ${selectedRecordingUrl === url ? "active" : ""}`}>
                                            <button type="button" className="caop-recording-open" onClick={() => setSelectedRecordingUrl(url)}>
                                                <span>{formatDateTime(item.starts_at)}</span>
                                                <span className="caop-recording-range">до {formatDateTime(item.ends_at)}</span>
                                            </button>
                                            <div className="caop-recording-meta">
                                                <span>{formatSize(item.file_size_bytes)}</span>
                                                <a href={url} target="_blank" rel="noreferrer">Файл</a>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                </div>
            ) : (
                <div className="caop-card caop-permissions-card">
                    <div className="caop-card-head">
                        <div>
                            <h3>Доступ квартир до історії камер</h3>
                            <p className="caop-muted">Без галочки клієнт цієї квартири не бачить історію камер, навіть якщо локація прив'язана до акаунта.</p>
                        </div>
                        <button type="button" className="add-btn refresh" onClick={handleSavePermissions} disabled={saving || permissionsLoading}>
                            <Save size={16} /> Зберегти доступи
                        </button>
                    </div>

                    <div className="caop-permissions-toolbar">
                        <input
                            value={permissionSearch}
                            onChange={(e) => setPermissionSearch(e.target.value)}
                            placeholder="Пошук по клієнту, квартирі або особовому рахунку"
                        />
                    </div>

                    {permissionsLoading ? (
                        <div className="caop-empty">Завантаження доступів...</div>
                    ) : !selectedLocation ? (
                        <div className="caop-empty">Виберіть конкретну локацію, щоб керувати доступом квартир.</div>
                    ) : filteredPermissions.length === 0 ? (
                        <div className="caop-empty">Для цієї локації ще немає активних клієнтських контекстів.</div>
                    ) : (
                        <div className="caop-permissions-list">
                            {filteredPermissions.map((item) => (
                                <label key={`${item.client_id}:${item.apartment || ""}`} className="caop-permission-item">
                                    <div className="caop-permission-content">
                                        <div className="caop-permission-title">{item.client_name}</div>
                                        <div className="caop-permission-meta">
                                            Квартира: {item.apartment || "не вказано"}
                                            {item.personal_account ? ` • Ос. рахунок: ${item.personal_account}` : ""}
                                        </div>
                                    </div>
                                    <span className="caop-permission-toggle">
                                        <input
                                            type="checkbox"
                                            checked={!!item.enabled}
                                            onChange={(e) => handlePermissionToggle(item.client_id, item.apartment, e.target.checked)}
                                        />
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CameraAccessOperator;